import React from 'react';
import { List, ListItem, ListItemIcon, ListItemText, Typography, Box } from '@mui/material';
import { CheckCircle as CheckIcon } from '@mui/icons-material';

/**
 * The ticked feature list, as the plan cards render it.
 *
 * ── WHY IT IS ITS OWN COMPONENT ─────────────────────────────────────────────
 * It lived inside PlanCard until the free-trial dialog needed the same list.
 * The brief for that dialog was "design should be similar as on manage plan
 * page", and the only way to guarantee similar is to render it with the same
 * code — a second copy is similar on the day it is written and drifts from
 * then on. utils/planFeatures.js already builds the ROWS for both; this is the
 * matching half that draws them.
 *
 * Rows are objects rather than strings because two of them carry a second
 * level: the credit row has a muted piece estimate beneath it, and the
 * flexibility row has indented bullets.
 */
/** Minimum readable width of one feature column, in px. */
const MIN_COLUMN_PX = 180;
/** Gap between feature columns, in px — a literal so the calc below can use it. */
const COLUMN_GAP_PX = 24;

/**
 * @param columns  The MOST columns to lay the rows out in. 1 — the default,
 *   and what the column cards use — leaves the list exactly as it was: one
 *   column, no grid.
 *
 *   The wide Basic row asks for four, because the same list that is a tall
 *   column inside a narrow card would otherwise be a tall column inside a
 *   full-width card, leaving most of the row empty — and every column it fits
 *   is a row off that card's height.
 *
 *   ── A CEILING, NOT A COUNT ─────────────────────────────────────────────────
 *   The list gets as many columns as fit at MIN_COLUMN_PX each, up to
 *   `columns`, measured against its OWN width rather than the viewport. That
 *   matters here because the app's sidebar is a fixed 256px: the same screen
 *   width can leave this list 400px or 800px, and a fixed three would come out
 *   a word or two per line at the narrow end.
 *
 *   The track minimum is the larger of MIN_COLUMN_PX and an exact 1/Nth share.
 *   Once there is room for N columns the share wins, so auto-fit can never make
 *   an N+1th; below that, MIN_COLUMN_PX wins and columns drop one at a time —
 *   down to one on a phone, with no media query.
 *
 *   CSS grid rather than `column-count`: multi-column would let a single ticked
 *   row break across a column boundary, so a tick could end one column and its
 *   label begin the next. Grid places whole items.
 */
export default function PlanFeatureList({ features = [], columns = 1 }) {
  const multiColumn = columns > 1;
  const share = `calc((100% - ${(columns - 1) * COLUMN_GAP_PX}px) / ${columns})`;

  return (
    <List
      dense
      disablePadding
      sx={multiColumn ? {
        display: 'grid',
        columnGap: `${COLUMN_GAP_PX}px`,
        gridTemplateColumns:
          `repeat(auto-fit, minmax(max(${MIN_COLUMN_PX}px, ${share}), 1fr))`,
      } : undefined}
    >
          {features.map((feature) => (
            <ListItem
              key={feature.label}
              disableGutters
              sx={{ py: 0.4, px: 0, alignItems: 'flex-start' }}
            >
              <ListItemIcon sx={{ minWidth: 30, mt: '2px' }}>
                <CheckIcon sx={{ fontSize: 20, color: '#1C7C73' }} />
              </ListItemIcon>
              <ListItemText
                disableTypography
                primary={(
                  <>
                    <Typography
                      variant="body2"
                      color="text.primary"
                      // The three rows that differ between tiers — the credit
                      // amount, the website allowance and priority support —
                      // carry the weight. Everything else is shared, so bolding
                      // it too would flatten the distinction back out.
                      sx={{ fontSize: '0.85rem', fontWeight: feature.strong ? 700 : 400 }}
                    >
                      {feature.label}
                    </Typography>

                    {feature.detail && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        // ONE claim, so one weight. "≈ 61 pieces of local SEO
                        // content with AI image" is a single sentence: it used
                        // to render as a bold half and a lighter half, which
                        // read as two statements and made "with AI image" look
                        // like a footnote to the estimate rather than part of
                        // what the estimate covers.
                        sx={{
                          fontSize: '0.8rem', mt: 0.15, fontStyle: 'italic', fontWeight: 700,
                        }}
                      >
                        {feature.detail}
                        {feature.detailTail ? ` ${feature.detailTail}` : ''}
                      </Typography>
                    )}

                    {feature.bullets && (
                      <Box component="ul" sx={{ listStyle: 'none', m: 0, mt: 0.4, pl: 1.25 }}>
                        {feature.bullets.map((bullet) => {
                          // A bullet is either a plain string or, on the Basic
                          // card's credit-pack row, a price with its own
                          // estimate beneath it. Accepting both keeps one
                          // renderer for a list that is otherwise identical
                          // across the four cards.
                          const text = typeof bullet === 'string' ? bullet : bullet.text;
                          const detail = typeof bullet === 'string' ? null : bullet.detail;
                          return (
                            <Box component="li" key={text} sx={{ mb: detail ? 0.5 : 0 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 0.75,
                                  color: 'text.secondary',
                                  fontSize: '0.8rem',
                                  lineHeight: 1.6,
                                }}
                              >
                                {/* aria-hidden: the dash is a bullet glyph, and a
                                    screen reader announcing "hyphen" before each
                                    item is noise. */}
                                <Box component="span" aria-hidden>–</Box>
                                {/* The price is the claim on this row, so it
                                    carries the weight — the same relationship
                                    the credit row has with its estimate. */}
                                <Box component="span" sx={{ fontWeight: detail ? 700 : 400 }}>
                                  {text}
                                </Box>
                              </Box>
                              {detail && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ fontSize: '0.78rem', fontStyle: 'italic', pl: 2 }}
                                >
                                  {detail}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </>
                )}
              />
            </ListItem>
          ))}
    </List>
  );
}
